import React, { memo, useCallback, useMemo } from "react";
import { v4 as uuidv4 } from "uuid";
import { Link, useLocation, useParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import Input from "./Inputs/Input";
import CompoundInput from "./Inputs/CompoundInput";
import JsonCollapse from "../../Components/JsonCollapse";
import {
  addNewMessageTemplate,
  selectMessageTemplates,
} from "../../../store/slices/projectSlice";
import Errors from "../../Components/Errors";
import { selectSurveyThemes } from "../../../store/slices/surveyThemesSlice";
import { faPenToSquare } from "@fortawesome/free-solid-svg-icons";

function ConfigurationTab({ dispatch, state }) {
  const themes = useSelector(selectSurveyThemes);
  const messageTemplates = useSelector(selectMessageTemplates);
  const storedispatch = useDispatch();

  const params = useParams();
  const location = useLocation();

  const messageTemplateOptions = useMemo(
    () => [
      {
        value: "",
        label: "No message",
      },
      ...messageTemplates.map((template) => ({
        value: template?.UUID,
        label: template?.Label,
      })),
    ],
    [messageTemplates]
  );

  const handleCreateMessageTemplate = useCallback(
    (value) => {
      const newUUID = uuidv4();
      storedispatch(
        addNewMessageTemplate({
          UUID: newUUID,
          Label: value,
          Text: value,
        })
      );
      dispatch({
        type: "message-template",
        MessageTemplateUUID: newUUID,
      });
    },
    [dispatch, storedispatch]
  );

  const handleMessageTemplateChange = useCallback(
    (values) => {
      dispatch({
        type: "message-template",
        MessageTemplateUUID: values[0],
      });
    },
    [dispatch]
  );

  const handleFormSubmitModeChange = useCallback(
    (values) => {
      dispatch({
        type: "form-submit-mode",
        FormSubmitMode: values[0],
      });
    },
    [dispatch]
  );

  const handleErrorPolicyChange = useCallback(
    (values) => {
      dispatch({ type: "error-policy", ErrorPolicy: values[0] || "fail_fast" });
    },
    [dispatch]
  );

  const handleErrorMessageChange = useCallback(
    (values) => {
      const value = values[0] || {};
      dispatch({
        type: "error-message",
        ErrorMessage: value.Value || "",
        ErrorMessageValueFrom: value.ValueFrom || "static",
        ErrorMessageMainThread: value.MainThread || false,
      });
    },
    [dispatch]
  );

  return (
    <>
      <div className="mt-3 mb-3">
        <label className="form-label">Workflow Label</label>
        <input
          type="text"
          className="form-control input-lg"
          value={state.Label}
          onChange={(e) =>
            dispatch({ type: "edit-label", data: e.target.value })
          }
        />
      </div>
      {/* create/select message template */}
      <div className="mb-3">
        <label className="form-label">Message template</label>
        {state.MessageTemplateUUID && (
          <Link
            className="ms-2"
            to={`/library/logic/builder/${params.projectID}/message-template/${state.MessageTemplateUUID}`}
            state={{ nextPage: location.pathname }}
          >
            <FontAwesomeIcon icon={faPenToSquare} />
          </Link>
        )}
        <Input
          inputType="select"
          options={messageTemplateOptions}
          isValidNewOption={(v) => v}
          onCreateOption={handleCreateMessageTemplate}
          value={[
            state.MessageTemplateUUID ||
              "Select a template or start typing to create a new one",
          ]}
          onChange={handleMessageTemplateChange}
        />
        <div className="text-danger mt-1">
          <Errors
            filterFn={(e) =>
              e.workflow?.UUID === state.UUID &&
              e.error?.field === "MessageTemplateUUID"
            }
            linkDisabled
          />
        </div>
      </div>
      <div className="mt-3 mb-3">
        <label className="form-label">Form submit mode</label>
        <Input
          inputType="radio"
          radioName="form-submit-mode"
          value={[state.FormSubmitMode || "once"]}
          onChange={handleFormSubmitModeChange}
          options={[
            {
              value: "once",
              label: "Allow form submit once",
            },
            {
              value: "running",
              label: "As long as the workflow is running",
            },
          ]}
        />
      </div>
      <div className="mt-3 mb-3">
        <label className="form-label">Default error policy</label>
        <Input
          inputType="select"
          isClearable={false}
          isValidNewOption={() => false}
          value={[state.ErrorPolicy || "fail_fast"]}
          onChange={handleErrorPolicyChange}
          options={[
            { value: "fail_fast", label: "Fail fast" },
            { value: "ignore", label: "Ignore and continue" },
            { value: "trigger", label: "Trigger error listener and stop" },
            {
              value: "trigger_and_continue",
              label: "Trigger error listener and continue",
            },
          ]}
        />
      </div>
      {["trigger", "trigger_and_continue"].includes(state.ErrorPolicy) && (
        <div className="mt-3 mb-3">
          <label className="form-label">Override error message (optional)</label>
          <CompoundInput
            condition={state}
            optionType="errorMessage"
            inputArgumentProps={{ Format: "short-text" }}
            isValidNewOption={(value) => value.trim()}
            values={[
              {
                Type: "errorMessage",
                Value: state.ErrorMessage || "",
                ValueFrom: state.ErrorMessageValueFrom || "static",
                MainThread: state.ErrorMessageMainThread || false,
              },
            ]}
            onChange={handleErrorMessageChange}
          />
        </div>
      )}
      <div className="mt-3 mb-3">
        <label className="form-label">Form title</label>
        <input
          type="text"
          className="form-control input-lg"
          value={state.SurveyJSON?.title}
          onChange={(e) =>
            dispatch({
              type: "edit-survey",
              data: { ...state.SurveyJSON, title: e.target.value },
            })
          }
        />
      </div>
      <div className="mt-3 mb-3">
        <label className="form-label">Form description</label>
        <input
          type="text"
          className="form-control input-lg"
          value={state.SurveyJSON?.description}
          onChange={(e) =>
            dispatch({
              type: "edit-survey",
              data: { ...state.SurveyJSON, description: e.target.value },
            })
          }
        />
      </div>
      <div className="mt-3 mb-3">
        <label className="form-label">Form complete message</label>
        <input
          type="text"
          className="form-control input-lg"
          value={state.SurveyJSON?.completedHtml}
          onChange={(e) =>
            dispatch({
              type: "edit-survey",
              data: { ...state.SurveyJSON, completedHtml: e.target.value },
            })
          }
        />
      </div>
      <div className="mt-3 mb-3">
        <label className="form-label">Form complete button label</label>
        <input
          type="text"
          className="form-control input-lg"
          value={state.SurveyJSON?.completeText}
          onChange={(e) =>
            dispatch({
              type: "edit-survey",
              data: { ...state.SurveyJSON, completeText: e.target.value },
            })
          }
        />
      </div>
      <div className="mt-3 mb-3">
        <label className="form-label">Form theme</label>
        <select
          className="form-select"
          value={state?.SurveyThemeUUID}
          onChange={(e) =>
            dispatch({
              type: "edit-survey-theme",
              data: e.target.value,
            })
          }
        >
          {themes.map((theme) => (
            <option value={theme.UUID} key={theme.UUID}>
              {theme.Label}
            </option>
          ))}
        </select>
      </div>
      <JsonCollapse json={state} />
    </>
  );
}

export default memo(ConfigurationTab);
